import * as Api from '@ViewModels';
import { StyleSheet, css } from 'aphrodite';
import { type FC, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useUserSession } from '../../../models/hooks/appStateHooks';
import { Button } from '../../../web/components-v2/Button';
import { ApiClient } from '../../services/api';
import { RecordingPlaybackReporter } from '../../services/recordingPlayback';
import { type FocusedLeadContact, type LeadCall, useLeadCallHistory } from '../data';
import { IconClock, IconFileText, IconPhone } from './icons';

export type PastCallsPanelProps = {
	companyId: string | undefined;
	/** The lead's contacts, so a call's `contactIds` can be shown as a name. */
	contacts?: FocusedLeadContact[];
	onSendToAI: (label: string, transcriptText: string) => void;
};

/** Every past phone call on the lead, newest first. */
export const PastCallsPanel: FC<PastCallsPanelProps> = ({ companyId, contacts, onSendToAI }) => {
	const {
		data: calls,
		isLoading,
		error,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
	} = useLeadCallHistory(companyId);
	const userSession = useUserSession();
	const [sendingId, setSendingId] = useState<string | null>(null);
	const [playingId, setPlayingId] = useState<string | null>(null);

	const reporterRef = useRef<RecordingPlaybackReporter>(null);
	if (!reporterRef.current) {
		reporterRef.current = new RecordingPlaybackReporter(new ApiClient(userSession));
	}

	const handleSend = async (view: LeadCall) => {
		const call = view.phoneCall;
		const transcriptionId = call?.transcriptionId;
		if (!transcriptionId || !call?.id) return;
		setSendingId(call.id);
		try {
			const result = await userSession.webServiceHelper.callAsync<Api.ITranscriptionSpeakerLines>(
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: { type: 'SpeakerLine' },
					urlPath: `transcription/${transcriptionId}/view`,
				}),
				'GET'
			);
			const lines = result?.speakerLines ?? [];
			if (lines.length === 0) {
				toast.error('Transcript is empty.');
				return;
			}
			const text = lines.map(l => `${l.speaker}: ${l.line}`).join('\n');
			const dateLabel = view.timestamp ? new Date(view.timestamp).toLocaleString() : 'a previous call';
			onSendToAI(dateLabel, text);
		} catch {
			toast.error('Failed to load transcript');
		} finally {
			setSendingId(null);
		}
	};

	const handleTogglePlay = (callId: string | undefined) => {
		if (!callId) return;
		setPlayingId(current => {
			if (current === callId) {
				reporterRef.current?.reset();
				return null;
			}
			reporterRef.current?.reset();
			return callId;
		});
	};

	return (
		<aside className={css(styles.panel)}>
			<header className={css(styles.header)}>
				<div className={css(styles.title)}>Past Calls</div>
				<div className={css(styles.subtitle)}>Every dial on this lead, newest first</div>
			</header>
			<div className={css(styles.list)}>
				{isLoading && <div className={css(styles.empty)}>Loading…</div>}
				{error && <div className={css(styles.empty)}>Couldn’t load past calls.</div>}
				{!isLoading && !error && (!calls || calls.length === 0) && (
					<div className={css(styles.empty)}>No past calls for this lead.</div>
				)}
				{!isLoading &&
					!error &&
					(calls ?? []).map((view, index) => (
						<CallRow
							key={view.id ?? `row-${index}`}
							view={view}
							contactName={nameForCall(view, contacts)}
							sending={sendingId === view.phoneCall?.id}
							playing={!!view.phoneCall?.id && playingId === view.phoneCall.id}
							onSend={() => void handleSend(view)}
							onTogglePlay={() => handleTogglePlay(view.phoneCall?.id)}
							onPlaybackStarted={() =>
								reporterRef.current?.reportPlay({
									phoneCallId: view.phoneCall?.id ?? '',
									surface: 'focusedViewPastCalls',
								})
							}
						/>
					))}
				{hasNextPage && (
					<Button
						variant='outline'
						size='sm'
						styles={[styles.loadMore]}
						disabled={isFetchingNextPage}
						onClick={() => void fetchNextPage()}
					>
						{isFetchingNextPage ? 'Loading…' : 'Load older calls'}
					</Button>
				)}
			</div>
		</aside>
	);
};

type CallRowProps = {
	view: LeadCall;
	contactName?: string;
	sending: boolean;
	playing: boolean;
	onSend: () => void;
	onTogglePlay: () => void;
	onPlaybackStarted: () => void;
};

const CallRow: FC<CallRowProps> = ({
	view,
	contactName,
	sending,
	playing,
	onSend,
	onTogglePlay,
	onPlaybackStarted,
}) => {
	const call = view.phoneCall ?? {};
	const when = view.timestamp ? new Date(view.timestamp) : undefined;
	const dateLabel = when
		? when.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
		: 'Unknown date';
	const timeLabel = when ? when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '';
	// `duration` is returned in milliseconds (~12,639 for a ~12.6s call).
	const durationLabel = formatDurationSeconds(call.duration != null ? Math.round(call.duration / 1000) : undefined);

	const dialedNumber = phoneDisplay(call.phoneNumber);
	const recordingUrl = call.recording?.publicUrl;

	return (
		<div className={css(styles.row)}>
			<div className={css(styles.rowTop)}>
				<div className={css(styles.rowIcon)}>
					<IconFileText size={14} />
				</div>
				<div className={css(styles.rowMain)}>
					<div className={css(styles.rowTitle)}>
						{dateLabel}
						{timeLabel && <span className={css(styles.rowTime)}> · {timeLabel}</span>}
					</div>
					<div className={css(styles.rowWho)}>
						<span className={css(styles.rowDirection)}>{directionLabel(call.direction)}</span>
						{contactName && <span className={css(styles.rowContact)}>{contactName}</span>}
						{dialedNumber && <span className={css(styles.rowNumber)}>{dialedNumber}</span>}
						{!contactName && !dialedNumber && (
							<span className={css(styles.rowNumber)}>
								{view.loggedManually ? 'Logged manually' : 'Unknown number'}
							</span>
						)}
					</div>
					<div className={css(styles.rowMeta)}>
						{view.outcome && <span className={css(styles.outcomeChip)}>{view.outcome}</span>}
						{view.rep && <span className={css(styles.metaChip)}>{view.rep}</span>}
						{durationLabel && (
							<span className={css(styles.metaChip)}>
								<IconClock size={10} />
								{durationLabel}
							</span>
						)}
						{!view.loggedManually && !recordingUrl && (
							<span className={css(styles.metaChip)}>
								<IconPhone size={10} />
								No recording
							</span>
						)}
					</div>
				</div>
			</div>
			<div className={css(styles.rowActions)}>
				{recordingUrl && (
					<Button variant={playing ? 'secondary' : 'outline'} size='sm' onClick={onTogglePlay}>
						{playing ? 'Close' : 'Play'}
					</Button>
				)}
				<Button
					variant='outline'
					size='sm'
					onClick={onSend}
					disabled={sending || !call.transcriptionId}
					title={!call.transcriptionId ? 'No transcript available' : 'Send transcript to AI conversation'}
				>
					{sending ? 'Sending…' : 'Send to AI'}
				</Button>
			</div>
			{playing && recordingUrl && (
				<audio className={css(styles.player)} src={recordingUrl} controls autoPlay onPlay={onPlaybackStarted}>
					<track kind='captions' />
				</audio>
			)}
		</div>
	);
};

function nameForCall(view: LeadCall, contacts: FocusedLeadContact[] | undefined): string | undefined {
	const id = view.phoneCall?.contactIds?.[0];
	if (!id) return undefined;
	return contacts?.find(c => c.id === id)?.name;
}

function directionLabel(direction: LeadCall['phoneCall']['direction']): string {
	return direction === 'Inbound' ? 'Inbound' : direction === 'Outbound' ? 'Outbound' : 'Call';
}

function formatDurationSeconds(seconds: number | undefined): string {
	if (!seconds || seconds <= 0) return '';
	if (seconds < 60) return `${seconds}s`;
	const m = Math.floor(seconds / 60);
	const s = seconds % 60;
	return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

function phoneDisplay(p: { standard?: string; e164?: string } | undefined): string | undefined {
	return p?.standard || p?.e164 || undefined;
}

const styles = StyleSheet.create({
	panel: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
		minHeight: 0,
		height: '100%',
		background: 'var(--bg)',
	},
	header: {
		padding: '12px 16px',
		borderBottom: '1px solid var(--border)',
		flexShrink: 0,
	},
	title: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: 'var(--ink-faint)',
	},
	subtitle: {
		fontSize: 12,
		color: 'var(--ink-muted)',
		marginTop: 2,
	},
	list: {
		flex: 1,
		overflowY: 'auto',
		minHeight: 0,
		padding: '8px 12px 16px',
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
	empty: {
		padding: '24px 14px',
		fontSize: 12.5,
		color: 'var(--ink-muted)',
		textAlign: 'center',
	},
	row: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		padding: '10px 12px',
		borderRadius: 8,
		background: 'var(--bg-elev)',
		border: '1px solid var(--border)',
	},
	rowTop: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 10,
	},
	rowIcon: {
		width: 26,
		height: 26,
		borderRadius: 6,
		background: 'var(--bg-sunken)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: 'var(--ink-muted)',
		flexShrink: 0,
	},
	rowMain: {
		flex: 1,
		minWidth: 0,
	},
	rowTitle: {
		fontSize: 13,
		fontWeight: 500,
		color: 'var(--ink)',
	},
	rowTime: {
		color: 'var(--ink-muted)',
		fontWeight: 400,
	},
	rowWho: {
		display: 'flex',
		alignItems: 'baseline',
		flexWrap: 'wrap',
		gap: 6,
		marginTop: 3,
		fontSize: 12.5,
		minWidth: 0,
	},
	rowDirection: {
		fontSize: 10,
		fontWeight: 600,
		letterSpacing: '0.05em',
		textTransform: 'uppercase',
		color: 'var(--ink-faint)',
	},
	rowContact: {
		fontWeight: 600,
		color: 'var(--ink)',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	rowNumber: {
		color: 'var(--ink-muted)',
	},
	rowMeta: {
		display: 'flex',
		gap: 6,
		marginTop: 4,
		flexWrap: 'wrap',
	},
	metaChip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		fontSize: 11,
		color: 'var(--ink-muted)',
		background: 'var(--bg-sunken)',
		padding: '2px 6px',
		borderRadius: 4,
	},
	rowActions: {
		display: 'flex',
		gap: 6,
		flexWrap: 'wrap',
	},
	player: {
		width: '100%',
		height: 32,
	},
	outcomeChip: {
		display: 'inline-flex',
		alignItems: 'center',
		fontSize: 11,
		fontWeight: 600,
		color: 'var(--ink)',
		background: 'var(--attr-bg)',
		padding: '2px 6px',
		borderRadius: 4,
	},
	loadMore: {
		alignSelf: 'center',
		marginTop: 4,
	},
});
